import { i18n } from '../i18n.js';
import { CopyText } from './copy-text.js';
import { DiscordLinks } from './discord-links.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { MoonfallGuest } from '../guest-session.js';
import { BootVeil } from './boot-veil.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * UI Lobby Module
 * Handles all UI interactions related to the game lobby
 */

import { ActionIntent } from './action-intent.js';
import { AssetPreloader } from './asset-preloader.js';
import { AvatarPicker } from './avatar-picker.js';
import { CardDrag } from './card-drag.js';
import { Cards } from './cards.js';
import { Collection } from './collection.js';
import { Confirm } from './confirm-dialog.js';
import { DeckSelector } from './deck-selector.js';
import { Effects } from './effects.js';
import { Feedback } from './feedback.js';
import { Firebase } from '../firebase-loader.js';
import { FoundryInventoryWarm } from './foundry-inventory-warm.js';
import { FoundrySurface } from './foundry-surface.js';
import { GameEnd } from './game-end.js';
import { HoverHandoff } from './hover-handoff.js';
import { Indicators } from './indicators.js';
import { LobbyQuestsToggle } from './lobby-quests-toggle.js';
import { MatchLoading } from './match-loading.js';
import { MoonExchange } from './moon-exchange.js';
import { HubSheets } from './hub-sheets.js';
import { LobbyCover } from './lobby-cover.js';
import { Mulligan } from './mulligan.js';
import { Platform } from './platform.js';
import { Quests } from './quests.js';
import { Referrals } from './referrals.js';
import { SoundEffects } from './sound-effects.js';
import { SpellAnnounce } from './spell-announce.js';
import { StarterReward } from './starter-reward.js';
import { Store } from './store.js';
import { StoryFeed } from './story-feed.js';
import { ThinkingIndicator } from './thinking-indicator.js';
import { TurnTimer } from './turn-timer.js';
import { TurnBanner } from './turn-banner.js';
import { Tutorial } from './tutorial.js';
import { GameState } from '../game-state.js';
import { SocketHandler } from '../socket-handler.js';
let Lobby = {};

// Create UI namespace if it doesn't exist

// Lobby module
Lobby = (function () {
    // DOM Elements
    const elements = {
        lobbyView: document.getElementById('lobby-view'),
        gameView: document.getElementById('game-view'),
        pvpGame: document.getElementById('pvp-game'),
        buttonRow: document.querySelector('#pvp-game .button-row'),
        roomActions: document.querySelector('#pvp-game .pvp-room-actions'),
        singlePlayerPanel: document.getElementById('single-player-panel'),
        adventureTile: document.getElementById('hub-adventure-tile'),
        friendsTile: document.getElementById('hub-friends-tile'),
        soloAdventureBtn: document.getElementById('solo-adventure-btn'),
        tutorialBtn: document.getElementById('tutorial-btn'),
        tutorialResumeMangaNote: document.getElementById('tutorial-resume-manga-note'),
        createRoomBtn: document.getElementById('create-room-btn'),
        roomInfo: document.getElementById('room-info'),
        roomInviteRow: document.querySelector('#room-info .room-invite-row'),
        roomCodeSpan: document.getElementById('room-code'),
        copyCodeBtn: document.getElementById('copy-code-btn'),
        copyLinkBtn: document.getElementById('copy-link-btn'),

        roomCodeInput: document.getElementById('room-code-input'),
        joinRoomBtn: document.getElementById('join-room-btn'),
        roomStateHead: document.getElementById('room-state-head'),
        waitingHost: document.getElementById('waiting-host'),
        joinRoomBar: document.getElementById('join-room-bar'),

        deckManager: document.getElementById('deck-manager'),
        openFoundryBtn: document.getElementById('open-foundry-btn'),
        openCollectionBtn: document.getElementById('open-collection-btn'),

        userControls: document.getElementById('user-controls'),
        username: document.getElementById('user-username'),
        discordLink: document.getElementById('pvp-discord-link'),
        feedbackLink: document.getElementById('feedback-link'),
        lobbyScroll: document.querySelector('#lobby-view .lobby-scroll'),
        utilityRail: document.getElementById('lobby-utility-rail'),
        languageCluster: document.getElementById('lobby-language-cluster'),

        returnLobbyBtn: document.getElementById('skip-tutorial-btn'),

        goBackBtn: document.getElementById('go-back-btn')
    };

    function usesCompactLobbyLayout() {
        return window.matchMedia?.(
            '(orientation: portrait) and (max-width: 768px), '
            + '(orientation: landscape) and (max-height: 560px)'
        )?.matches === true;
    }

    /* The landscape-tablet band, mirrored from the media query that owns the
       tablet top-bar chrome in mobile.css. Kept separate from
       `usesCompactLobbyLayout()`: a tablet takes the phone's PLACEMENT for
       Quests, Invite and Feedback without taking its abbreviated labels. */
    function usesTabletLobbyChrome() {
        return window.matchMedia?.(
            '(orientation: landscape) and (min-width: 960px) and '
            + '(min-height: 561px) and (max-width: 1560px) and '
            + '(max-aspect-ratio: 17/10)'
        )?.matches === true;
    }

    /* Mirrors the fixed-cluster media query in language-selector.css. Squarish
       landscape tablets fall through both this and the compact test, and that
       band is exactly the one whose cluster lane is not fixed-positioned. */
    function usesDesktopLobbyLayout() {
        return window.matchMedia?.(
            '(orientation: landscape) and (min-height: 561px) and '
            + '(min-width: 1101px) and (min-aspect-ratio: 1601/1000), '
            + '(orientation: landscape) and (min-height: 561px) and (min-width: 1367px)'
        )?.matches === true;
    }

    function keepActiveRoomControlsVisible() {
        if (elements.roomInfo?.style.display !== 'flex') return;
        const reveal = () => {
            if (elements.roomInfo?.style.display !== 'flex') return;
            elements.roomInfo.scrollIntoView?.({ block: 'end', inline: 'nearest' });
        };
        if (typeof window.requestAnimationFrame !== 'function') {
            reveal();
            return;
        }
        // Locale text, footer measurement, and viewport-driven utility
        // reparenting can all settle on adjacent frames. Align only after both
        // have committed so the created-room actions never land fractionally
        // below the visible lobby scrollport in Chromium.
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(reveal);
        });
    }

    /**
     * The responsive strings the lobby still swaps per layout. Discord and
     * the invitation no longer move at all: the hub gives both ONE home on
     * every layout — the Community sheet (hub-sheets.js) — and the Moon
     * Foundry tile has room for its full name everywhere.
     */
    function syncUtilityPlacement() {
        const compact = usesCompactLobbyLayout();
        if (elements.roomCodeInput) {
            const placeholderKey = compact ? 'lobby.roomCodeShort' : 'lobby.enterRoomCode';
            const placeholder = compact ? 'Code' : 'Enter room code';
            elements.roomCodeInput.setAttribute('data-i18n-placeholder', placeholderKey);
            elements.roomCodeInput.placeholder = uiText(placeholderKey, placeholder);
        }
        keepActiveRoomControlsVisible();
    }

    /* `placeFeedbackLink()` used to live here.

       It moved the Feedback pill between three homes — the fixed chrome
       cluster on desktop/tablet/phone, the utility rail otherwise — and
       re-ran on every breakpoint change, because a free-floating pill in the
       top-right corner has no single place it fits. Feedback is a row in the
       chrome menu now, so it has exactly one home and nothing has to shuffle
       it. Deleting this also fixed a real bug the change introduced: the
       function kept stealing the button BACK out of the menu on the first
       resize, which the survey caught as Feedback painting as a lone 48x48
       pill beside a menu that also contained it. */

    /**
     * Initialize event listeners for lobby elements
     */
    const activeDeckStartButtons = new WeakSet();
    const battleAudioWarmButtons = new WeakSet();
    let returnLobbyControlBound = false;
    let activeGameTransition = null;

    function beginGameTransition({ deck = null } = {}) {
        if (activeGameTransition) return null;
        const token = {};
        activeGameTransition = token;
        // A match can spend this month's manga; the next picker asks again.
        forgetBattleStatuses();
        /* And it reserves Moonlight on the game server, out of this client's
           sight: the answer the client shares for a few seconds
           (convex-client.js) is from before it, so a read taken from here on
           asks again. Not re-read now — the reservation is still being made. */
        MoonfallCloud?.users?.forgetMoonlightStatus?.();
        document.body?.classList.add('game-transition-pending');
        // An expanded Quests sheet must not survive into a battle.
        LobbyQuestsToggle?.close?.();
        safelyCloseLobbySurface('Referrals', () => Referrals?.close?.({
            restoreFocus: false,
        }));
        /* THE PRESS IS ANSWERED BY THE LOADING SCREEN, IN THIS TASK. The
           server takes seconds to build a match (3.3 s measured at 110 ms RTT)
           and nothing on screen used to change in that time; the screen also
           starts the battle's art while the server works
           (ui/match-loading.js). */
        runOptionalGameTransitionStep(
            'Match loading screen',
            () => MatchLoading?.begin?.(deck ? { deck } : {}),
        );
        return token;
    }

    function cancelGameTransition(token) {
        if (!token || token !== activeGameTransition) return false;
        activeGameTransition = null;
        document.body?.classList.remove('game-transition-pending');
        runOptionalGameTransitionStep('Match loading screen', () => MatchLoading?.cancel?.());
        return true;
    }

    function finishGameTransition() {
        activeGameTransition = null;
        document.body?.classList.remove('game-transition-pending');
    }

    function runOptionalGameTransitionStep(name, step) {
        try {
            const result = step();
            result?.catch?.((error) => {
                console.error(`[Lobby] ${name} failed:`, error);
            });
        } catch (error) {
            console.error(`[Lobby] ${name} failed:`, error);
        }
    }

    function normalizeAcknowledgedScopeId(value) {
        if (typeof value !== 'string' || !value || value.length > 128) return null;
        return value === value.trim() ? value : null;
    }

    function pinAcknowledgedGameStart(acknowledgement) {
        const roomId = normalizeAcknowledgedScopeId(acknowledgement?.roomId);
        if (!roomId) return false;
        const matchId = acknowledgement?.matchId == null
            ? null
            : normalizeAcknowledgedScopeId(acknowledgement.matchId);
        const playerId = acknowledgement?.playerId == null
            ? null
            : normalizeAcknowledgedScopeId(acknowledgement.playerId);
        if (
            (acknowledgement?.matchId != null && !matchId)
            || (acknowledgement?.playerId != null && !playerId)
        ) return false;

        try {
            SocketHandler?.pinAcknowledgedStart?.({
                roomId,
                ...(matchId ? { matchId } : {}),
                ...(playerId ? { playerId } : {}),
            });
        } catch (error) {
            console.error('[Lobby] Failed to pin the acknowledged socket scope:', error);
        }

        const scope = {
            roomId,
            ...(matchId ? { matchId } : {}),
            ...(playerId ? { playerId } : {}),
        };
        try {
            GameState?.update?.(scope);
            return GameState?.get?.()?.roomId === roomId;
        } catch (error) {
            console.error('[Lobby] Failed to pin the acknowledged game scope:', error);
            return false;
        }
    }

    function failClosedStartedGame(token, acknowledgement) {
        if (!token || token !== activeGameTransition) return false;
        console.error(
            '[Lobby] The server started a game, but the gameStarted view was not installed. '
            + 'Failing closed into the game surface.'
        );
        // No board is coming to present: the recovery message must be seen.
        runOptionalGameTransitionStep('Match loading screen', () => MatchLoading?.cancel?.());
        pinAcknowledgedGameStart(acknowledgement);
        try {
            Lobby?.switchToGameView?.({ veil: false });
            if (!document.body?.classList.contains('game-active')) {
                throw new Error('The game surface did not become active.');
            }
        } catch (error) {
            console.error('[Lobby] Failed to establish the game recovery surface:', error);
            elements.lobbyView.style.display = 'none';
            document.body.classList.remove('lobby-active');
            elements.lobbyView.style.setProperty('display', 'none', 'important');
            elements.gameView.style.display = 'flex';
            document.body?.classList.add('game-active');
            finishGameTransition();
        }

        const message = uiText(
            'gameEnd.fatalError.message',
            'A critical AI service error occurred. Please refresh the page.'
        );
        try {
            if (typeof GameEnd?.showFatalError === 'function') {
                GameEnd.showFatalError(message);
            } else {
                alert(message);
            }
        } catch (error) {
            console.error('[Lobby] Failed to show the game recovery message:', error);
        }
        return true;
    }

    function isIndeterminateGameStart(result) {
        return result?.ok === false
            && (
                result.code === 'timeout'
                || result.code === 'no_response'
                || result.code === 'late_timeout'
            );
    }

    function awaitIndeterminateGameStart(token, result) {
        if (!token || token !== activeGameTransition) return false;
        /* The server did not answer in time. The player has been told so, and
           the lobby is what they are looking at; a board that turns up late
           installs itself (and covers itself) as any other gameStarted. */
        runOptionalGameTransitionStep('Match loading screen', () => MatchLoading?.cancel?.());
        const lateAck = result?.lateAck;
        if (!lateAck || typeof lateAck.then !== 'function') return true;
        Promise.resolve(lateAck).then((lateResult) => {
            if (!token || token !== activeGameTransition) return;
            if (lateResult?.ok === true) {
                failClosedStartedGame(token, lateResult);
                return;
            }
            if (!isIndeterminateGameStart(lateResult)) {
                cancelGameTransition(token);
            }
        }).catch((error) => {
            console.error('[Lobby] Late game-start acknowledgement failed:', error);
        });
        return true;
    }

    /**
     * A START THAT NEVER REACHED THE SERVER IS OFFERED AGAIN, HERE.
     *
     * socket-handler.js hands such a start back marked `unreached` (no
     * connection inside its 10 s, or a preflight that hung) and says nothing
     * itself. It used to raise a native alert, "Failed to connect.", and leave
     * the player to press the button, choose the deck and start all over; a
     * phone browser may suppress that alert and show nothing at all
     * (ui/confirm-dialog.js). The lobby asks in the app's own dialog instead,
     * and "Try again" runs the same start with the same choice.
     *
     * Asked only once the start's transition is cancelled, so nothing is left
     * pending under the question, and only for that mark: a refusal the
     * server gave, or a start it may still be working on, is never run twice.
     *
     * @returns {Promise<boolean>} whether the player asked for another try
     */
    async function playerRetriesUnreachedStart(result) {
        if (result?.ok !== false || result.unreached !== true) return false;
        return (await Confirm.ask(
            uiText('common.failedToConnect', 'Failed to connect.'),
            { confirmLabel: uiText('common.tryAgain', 'Try again') },
        )) === true;
    }

    async function runGameAdmission(start) {
        for (;;) {
            const transitionToken = beginGameTransition();
            if (!transitionToken) return { ok: false, code: 'transition_busy' };
            let result;
            try {
                result = await start();
                if (isIndeterminateGameStart(result)) {
                    awaitIndeterminateGameStart(transitionToken, result);
                } else if (gameStartFailed(result)) {
                    cancelGameTransition(transitionToken);
                } else {
                    // If gameStarted already installed the game, switchToGameView()
                    // retired this token and this becomes an intentional no-op.
                    failClosedStartedGame(transitionToken, result);
                }
            } catch (error) {
                cancelGameTransition(transitionToken);
                throw error;
            }
            if (!(await playerRetriesUnreachedStart(result))) return result;
        }
    }

    function safelyCloseLobbySurface(name, closeSurface) {
        try {
            const result = closeSurface();
            if (result && typeof result.catch === 'function') {
                result.catch((error) => {
                    console.error(`[Lobby] ${name} teardown failed:`, error);
                });
            }
        } catch (error) {
            console.error(`[Lobby] ${name} teardown failed:`, error);
        }
    }

    function closeLobbySurfacesForGame({ includeTransientSurfaces = true } = {}) {
        safelyCloseLobbySurface('Referrals', () => Referrals?.close?.({
            restoreFocus: false,
        }));
        safelyCloseLobbySurface('Store', () => Store?.close?.({
            immediate: true,
            restoreFocus: false,
        }));
        safelyCloseLobbySurface('Collection', () => Collection?.close?.({
            restoreFocus: false,
        }));
        if (includeTransientSurfaces) {
            safelyCloseLobbySurface('Deck Selector', () => DeckSelector?.close?.({
                outcome: { kind: 'superseded' },
                restoreFocus: false,
            }));
            safelyCloseLobbySurface('Feedback', () => Feedback?.close?.({
                restoreFocus: false,
            }));
        }
        safelyCloseLobbySurface('Foundry', () => {
            if (FoundrySurface?.isOpen?.()) {
                return closeFoundrySurface({ restoreFocus: false });
            }
            return false;
        });
        /* The hub sheets are aria-modal dialogs, and the one a match starts
           from used to stay open inside the hidden lobby for the whole match.
           WebKit and Firefox still compute it as displayed, so StoryFeed took
           it for a modal owning input: no narration opened by itself and no
           story square answered a press ("text no longer self expands …
           tapping the tiles was no longer showing the text", 2026-09-22).
           Every departure from the lobby shuts them. */
        safelyCloseLobbySurface('Hub sheets', () => HubSheets?.closeAll?.());
    }

    function focusViewDestination(view, preferredSelector) {
        const target = (preferredSelector && view?.querySelector?.(preferredSelector)) || view;
        if (!target?.isConnected || typeof target.focus !== 'function') return;
        if (target === view && !target.hasAttribute('tabindex')) target.tabIndex = -1;
        try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
    }

    function gameStartFailed(result) {
        return result === false || result?.ok === false;
    }

    /* The battle cues wait for the board. Their downloads used to run under
       the match loading screen next to the board's art — 13 cues, each fetched
       by the buffer and two voices, on a phone's 12 Mbps link (25 September
       2026). The gesture still resumes audio at once (SoundEffects); the bytes
       follow the first screen, or an idle moment if no match starts. */
    const BATTLE_AUDIO_IDLE_MS = 8000;
    let battleAudioGate = null;

    function battleAudioReady() {
        if (!battleAudioGate) {
            let open = null;
            const promise = new Promise((resolve) => { open = resolve; });
            const timer = setTimeout(() => open('idle'), BATTLE_AUDIO_IDLE_MS);
            battleAudioGate = {
                promise,
                open: (reason) => { clearTimeout(timer); open(reason); },
            };
        }
        return battleAudioGate.promise;
    }

    function openBattleAudioGate(after) {
        const gate = battleAudioReady();
        const current = battleAudioGate;
        Promise.resolve(after).then(() => current.open('presented'), () => current.open('presented'));
        return gate;
    }

    function prewarmBattleAudio() {
        try {
            const warming = SoundEffects?.preloadBattle?.({ deferUntil: battleAudioReady() });
            warming?.catch?.((error) => {
                console.warn('[Lobby] Battle audio prewarm failed:', error);
            });
        } catch (error) {
            console.warn('[Lobby] Battle audio prewarm failed:', error);
        }
    }

    /* The battle's shared art — the stock card back, the card frames and
       faces — on the same intent as its audio: a player reaching for a match
       button is seconds from a board (ui/match-loading.js). */
    function prewarmBattleChrome() {
        try {
            MatchLoading?.warmChrome?.();
        } catch (error) {
            console.warn('[Lobby] Battle art prewarm failed:', error);
        }
    }

    function bindBattleAudioWarmup(button) {
        if (!button || battleAudioWarmButtons.has(button)) return;
        battleAudioWarmButtons.add(button);
        button.addEventListener('pointerdown', prewarmBattleAudio, { passive: true });
        button.addEventListener('pointerenter', prewarmBattleAudio, { passive: true });
        button.addEventListener('focus', prewarmBattleAudio);
        button.addEventListener('pointerdown', prewarmBattleChrome, { passive: true });
        button.addEventListener('pointerenter', prewarmBattleChrome, { passive: true });
        button.addEventListener('focus', prewarmBattleChrome);
    }

    /**
     * The lone in-game exit control is shared by every match, but during the
     * anonymous landing-page guest tutorial it is a one-press "Skip Tutorial"
     * escape hatch, not the confirm-guarded exit. Keep the label in lockstep
     * with that behavior: swap BOTH the visible text and the `data-i18n` key so
     * a later language change re-translates the correct wording. Signed-in play
     * and the signed-in tutorial keep the "Exit Match" wording — its own key,
     * `game.exitMatch`, because the result screen's `gameEnd.returnToLobby`
     * ("Go to Lobby") describes leaving a match that is already over, while
     * this control abandons one still in progress.
     *
     * The fallback below MUST match `game.exitMatch` in ui.json. The old
     * fallback said "Return to Lobby" while the translation said "Go to
     * Lobby", so whenever this ran before translations resolved — which is the
     * normal case on a cold load — the button painted the old copy and only
     * some sessions ever corrected it. A fallback that disagrees with its own
     * key is a second, silent source of truth.
     */
    function syncReturnControlLabel() {
        const btn = elements.returnLobbyBtn;
        if (!btn) return;
        const guestSkip = MoonfallGuest?.isActive?.() === true;
        const key = guestSkip ? 'game.skipTutorial' : 'game.exitMatch';
        const fallback = guestSkip ? 'Skip Tutorial' : 'Exit Match';
        btn.setAttribute('data-i18n', key);
        btn.textContent = uiText(key, fallback);
        /* THE ARIA LABEL IS THE ACCESSIBLE NAME ON A PHONE, not a backstop.
           index.html calls it one, and that is true only where the text node is
           readable: `aria-label` OVERRIDES textContent, and the phone battle
           collapses this control to a 36px ✕ with `font-size: 0`. So the whole
           name rides on the attribute there — and it stayed on the exit
           wording for the entire guest tutorial, where the button is Skip.
           Measured in WebKit at 844x280 right after a real tap ended the
           tutorial. */
        btn.setAttribute('data-i18n-aria-label', key);
        btn.setAttribute('aria-label', uiText(key, fallback));
    }

    function bindInGameReturnControl() {
        if (!elements.returnLobbyBtn || returnLobbyControlBound) return;
        returnLobbyControlBound = true;
        syncReturnControlLabel();
        elements.returnLobbyBtn.addEventListener('click', async () => {
            if (MoonfallGuest?.isActive?.()) {
                MoonfallGuest.endGuestTutorial('skip');
                return;
            }
            const message = uiText(
                'game.confirmReturnLobby',
                'Are you sure you want to return to the lobby? The current game will be ended.'
            );
            /* Our own dialog, not window.confirm. A phone browser may suppress
               the native one silently and return false, which is precisely how
               this button was reported dead twice while being present and
               hittable the whole time. */
            const confirmed = await Confirm.ask(message, {
                confirmLabel: uiText('game.exitMatch', 'Exit Match'),
                destructive: true,
            });
            if (confirmed) Lobby.switchToLobbyView();
        });
    }

    function runGuardedLobbyAction(button, asyncFn, labelKey, fallbackLabel, cooldownMs = 0) {
        const guard = MoonfallClickGuard;
        if (!guard || typeof guard.guard !== 'function') {
            return Promise.resolve().then(asyncFn);
        }
        return guard.guard(button, asyncFn, {
            cooldownMs,
            label: uiText(labelKey, fallbackLabel),
        });
    }

    function guardLobbyButton(button, asyncFn, labelKey, fallbackLabel, cooldownMs) {
        if (!button) return;
        button.addEventListener('click', () => {
            runGuardedLobbyAction(button, asyncFn, labelKey, fallbackLabel, cooldownMs).catch((err) => {
                console.error('[Lobby] guarded action failed:', err);
            });
        });
    }

    /* One retry before giving up. A single failed entitlement read used to make
       a paying subscriber's Manga switch render dark with the Manga Pass upsell
       under it — the status is what decides whether the toggle defaults ON, so
       losing it is not a cosmetic loss. An undefined return still means
       "unknown", which the control now renders as unknown rather than as a
       verdict. */
    async function loadMangaGameStatus() {
        for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
                const status = await MoonfallCloud?.users?.getMangaGameStatus?.();
                if (status) return status;
            } catch (error) {
                console.warn('[Lobby] Manga preference status unavailable:', error);
            }
        }
        return undefined;
    }

    /* Lucid eligibility is the SUBSCRIPTION, and the client already reads it:
       `getMoonlightStatus` returns `subscriptionType`, so this needs no second
       Convex round-trip and no new function. `undefined` is returned on failure
       so the control renders its "checking your plan" state rather than telling
       a subscriber they need to buy what they already have — the server's
       `resolveGameTextProfile` stays the authority either way. */
    /* Only send a key the player actually chose. The server reads an ABSENT
       `includeManga` as the pre-preference default rather than as off, so
       padding the payload with `false` would silently change behaviour for
       anyone whose control never mounted. All three start paths take
       `options = {}`, so `undefined` is the same as passing nothing. */
    /* Returns undefined, and the callers then pass NO second argument at all.
       Passing `undefined` explicitly is not equivalent: four lifecycle tests
       assert `toHaveBeenCalledWith(deck)`, and an extra `undefined` argument
       fails that even though the runtime behaviour is identical. Keeping the
       arity honest is cheaper than loosening four assertions. */
    function battleOptions(includeManga, lucid) {
        const options = {};
        if (typeof includeManga === 'boolean') options.includeManga = includeManga;
        if (typeof lucid === 'boolean') options.lucid = lucid;
        return Object.keys(options).length ? options : undefined;
    }

    const LUCID_TIERS = new Set(['moonfall_storyteller', 'moonfall_manga']);
    async function loadLucidStatus() {
        try {
            const status = await MoonfallCloud?.users?.getMoonlightStatus?.();
            if (!status || typeof status.subscriptionType !== 'string') return undefined;
            return { eligible: LUCID_TIERS.has(status.subscriptionType) };
        } catch (error) {
            console.warn('[Lobby] Lucid preference status unavailable:', error);
            return undefined;
        }
    }

    /* ---- THE PICKER IS WARM BEFORE THE PRESS --------------------------------

       Start Adventure used to await the Manga status (with its retry) and
       THEN the Moonlight status before it even asked the picker to open:
       measured at ~620 ms of nothing after the press on a 110 ms link. Both
       reads are now taken ahead of the press — once the boot veil has lifted,
       and again on intent — side by side, memoised for a short window, and
       handed to the picker as answers or, while still in flight, as the reads
       themselves (the picker folds those into its one loading state instead
       of waiting here). A read that failed is not remembered, and a changed
       plan, a new identity or a started match forgets them all. */
    const BATTLE_STATUS_FRESH_MS = 30000;
    const battleStatusReads = { manga: null, lucid: null };

    function readBattleStatus(name, load) {
        const current = battleStatusReads[name];
        if (
            current
            && Date.now() - current.startedAt < BATTLE_STATUS_FRESH_MS
            && !(current.settled && current.value === undefined)
        ) {
            return current;
        }
        const read = { startedAt: Date.now(), settled: false, value: undefined, promise: null };
        read.promise = Promise.resolve(load()).then((value) => {
            read.settled = true;
            read.value = value;
            return value;
        });
        battleStatusReads[name] = read;
        return read;
    }

    /** The answer once there is one, the read in flight until then. */
    function battleStatusFor(read) {
        return read.settled ? read.value : read.promise;
    }

    function forgetBattleStatuses() {
        battleStatusReads.manga = null;
        battleStatusReads.lucid = null;
    }

    /* The Moonlight moved where this client could not see it. Everyone who
       shows or shares it listens: the chip re-reads (main.js), the shared
       answer is dropped (convex-client.js), the picker's statuses too. */
    function announceMoonlightMoved() {
        try {
            window.dispatchEvent(new CustomEvent('moonfall:moonlight-stale'));
        } catch (error) {
            console.warn('[Lobby] Could not announce the Moonlight change:', error);
        }
    }

    function warmBattleStatuses() {
        return {
            manga: readBattleStatus('manga', loadMangaGameStatus),
            lucid: readBattleStatus('lucid', loadLucidStatus),
        };
    }

    /* Everything the picker needs, ahead of the press. The picker memoises
       its own warm (the account's summoners whole, decoded portraits, every
       summoner's decks, its faces). Resolves once its first page is ready. */
    function warmDeckSelection({ statuses = true } = {}) {
        if (statuses) warmBattleStatuses();
        try {
            return Promise.resolve(DeckSelector?.warm?.()).catch(() => false);
        } catch (error) {
            console.warn('[Lobby] Deck selector warm failed:', error);
            return Promise.resolve(false);
        }
    }

    const deckSelectionWarmTargets = new WeakSet();
    function bindDeckSelectionWarmup(target) {
        if (!target || deckSelectionWarmTargets.has(target)) return;
        deckSelectionWarmTargets.add(target);
        target.addEventListener('pointerenter', warmDeckSelection, { passive: true });
        target.addEventListener('focus', warmDeckSelection);
        target.addEventListener('touchstart', warmDeckSelection, { passive: true });
    }

    /* A LOBBY WARM IS FOR A LOBBY THE PLAYER IS LOOKING AT.
       A match on screen, one being started or one being resumed owns the
       screen and the link, and nothing the lobby reads ahead is for it. Every
       idle warm asks this at the moment it runs, not when it was scheduled: a
       reload into a match schedules them with the rest of the sign-in and
       then boots to the board. Before this check the whole lobby warm — the
       Foundry inventory, the Collection page, the Store's reads — ran under
       the match on every such reload (12 of 12 measured, 26 September 2026). */
    const LOBBY_BUSY_BODY_CLASSES = Object.freeze([
        'game-active',
        'game-transition-pending',
        'game-resuming',
    ]);

    function lobbyIsIdle(ignoring = null) {
        const view = elements.lobbyView;
        if (!view?.isConnected || view.style.display === 'none') return false;
        const body = document.body;
        return !LOBBY_BUSY_BODY_CLASSES.some((name) => name !== ignoring && body?.classList.contains(name));
    }

    /* A RESUME IS "NOT YET", NOT "NEVER".
       A mid-match reload whose resume is slow (a phone link, a server that is
       restarting) has the boot veil show the lobby at its cap
       (socket-handler GAME_RESUME_BOOT_HOLD_MS) while the resume still
       retries. Only its end says whose screen this is: a board installed
       over the lobby (game-active), or a refusal that leaves the player on it
       (the class simply goes; retireUnresumableGame). A warm that finds
       nothing but a resume in the way waits for that end, once: dropping it
       left a refused resume's lobby cold for the whole session (review, 27
       September 2026). One watcher serves every waiting warm. Capped past the
       resume's whole retry ladder (nine asks of up to 10 s each and ~45 s of
       backoff); a resume still going then has given up, and the warms go. */
    const RESUME_WARM_WAIT_CAP_MS = 180_000;
    let resumeEnd = null;

    function whenResumeEnds() {
        if (resumeEnd) return resumeEnd;
        resumeEnd = new Promise((resolve) => {
            const classes = document.body.classList;
            let observer = null;
            let cap = null;
            const finish = () => {
                observer?.disconnect();
                clearTimeout(cap);
                resumeEnd = null;
                resolve();
            };
            // No watcher to be had: asked again at once, the warms go as before.
            if (typeof MutationObserver !== 'function') return void finish();
            observer = new MutationObserver(() => {
                if (!classes.contains('game-resuming') || classes.contains('game-active')) finish();
            });
            observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
            cap = setTimeout(finish, RESUME_WARM_WAIT_CAP_MS);
        });
        return resumeEnd;
    }

    /**
     * `warm`, run only if the lobby is still idle when its moment comes. A
     * moment that finds only a resume in the way asks again when the resume
     * ends, through `schedule`, so the warm keeps its slot (an idle one, or
     * after the page's `load`) rather than running inside the refusal.
     *
     * A SURFACE OVER THE LOBBY IS "NOT YET" TOO. The Collection, the Foundry,
     * the Store, the picker, Feedback or Referrals open over the lobby is the
     * screen the player is looking at, and a warm made for the lobby took its
     * link: on 1 Mbps the picker's warm started four portraits (1.8 MB) 0.45 s
     * after the Collection opened, and the Collection's own pictures missed
     * their 30 s attempt window (28 September 2026, the Collection's slow-link
     * spec). A moment that finds one open asks again once it closes, however
     * long it stays open (the player's own screen, not a hang), through
     * `schedule`. One watcher serves every waiting warm (LobbyCover).
     */
    function whileLobbyIdle(name, warm, schedule = (run) => run()) {
        let waited = false;
        const attempt = () => {
            if (!lobbyIsIdle()) {
                if (!waited && lobbyIsIdle('game-resuming')) {
                    waited = true;
                    void whenResumeEnds().then(() => schedule(attempt));
                }
                return;
            }
            if (LobbyCover.isCovered()) {
                void LobbyCover.whenUncovered().then(() => schedule(attempt));
                return;
            }
            try {
                warm();
            } catch (error) {
                console.warn(`[Lobby] ${name} warm failed:`, error);
            }
        };
        return attempt;
    }

    /** After the page's own `load`: a speculative read never competes with it. */
    function afterPageLoad(run) {
        if (document.readyState === 'complete') setTimeout(run, 0);
        else window.addEventListener('load', run, { once: true });
    }

    /** In an idle slot (capped at 2 s); without idle callbacks (Safari), shortly. */
    function inIdleSlot(run) {
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 2000 });
        else setTimeout(run, 200);
    }

    /**
     * What the signed-in lobby reads ahead of a press nobody has made yet
     * (docs/asset-loading.md law 3), once the boot veil lifts: while it is up
     * the link belongs to the lobby's own reads, and the veil waits for those
     * (ui/lobby-arrival.js). main.js calls this once the account strip is up.
     *
     * - The Store's three reads, so its open paints the finished store at
     *   once; and, after the page's `load`, its five product pictures, so the
     *   open is a swap, not a download.
     * - The Exchange's unlock answer, so its sell column is printed whole on
     *   the first frame of the tab (MoonExchange.prepare).
     * - The Collection's first pages, in an idle slot at low priority: three
     *   pages of the tab it opens on in one read, the other card tab's first
     *   page and the card backs, their pictures as bytes, the first page's
     *   first, so the Collection opens finished instead of over a page of
     *   skeletons and its next pages are already in (Collection.warmForLobby;
     *   the owner, 28 September 2026: "from the lobby, we could preload like
     *   three pages of anything"). Again on every return from a match.
     * - The Foundry's prices, so its canvas hint is printed with them. NOT its
     *   inventory: that is every card the player owns. The owner's rule (26
     *   September 2026): "it's fine to load things, but not a players entire
     *   library". The inventory is read on intent — hover, focus or the first
     *   touch of the Foundry tile (warmFoundry) — or by the surface's press.
     *
     * Each is checked when it runs (lobbyIsIdle). One skipped because a match
     * took the screen is not queued for later: its screen still warms on
     * intent and reads on its press. One that meets a resume still being
     * asked for waits for its answer, and one that meets a surface open over
     * the lobby waits for it to close (whileLobbyIdle).
     */
    function warmAtIdle() {
        return BootVeil.whenReleased().then(() => {
            whileLobbyIdle('Store data', () => Store?.warmData?.())();
            afterPageLoad(whileLobbyIdle('Store art', () => Store?.warmArt?.(), afterPageLoad));
            afterPageLoad(whileLobbyIdle('Exchange config', () => { void MoonExchange?.warmConfig?.(); }, afterPageLoad));
            afterPageLoad(() => inIdleSlot(whileLobbyIdle('Collection', () => Collection?.warmForLobby?.(), inIdleSlot)));
            afterPageLoad(whileLobbyIdle('Foundry prices', () => FoundryInventoryWarm?.warmConfig?.(), afterPageLoad));
        });
    }

    /* Once the lobby is quiet: after the boot veil (while it is up, the link
       belongs to what the lobby itself needs), in an idle slot. Not under Data
       Saver, and not for a lobby a match has replaced or is about to
       (lobbyIsIdle); intent and the press still warm it for them. Under a
       surface open over the lobby it waits for the surface to close
       (whileLobbyIdle), and the portraits it has on the wire then yield to
       that surface (deck-selector.js). */
    function warmDeckSelectionWhenIdle({ statuses = true } = {}) {
        /* No idle callback (Safari, and WebKit on iOS): start as the veil lifts.
           The Adventure is the lobby's first button, and a 1.5 s hold here put
           its four summoner portraits (~835 KB each) behind every other warm;
           measured 25 September 2026 on the WebKit phone seat, they were still
           arriving 2-3 s after the player opened the selector. */
        const idle = typeof window.requestIdleCallback === 'function'
            ? (fn) => window.requestIdleCallback(fn, { timeout: 4000 })
            : (fn) => setTimeout(fn, 0);
        void BootVeil.whenReleased().then(() => idle(whileLobbyIdle('Deck selection', () => {
            let saveData = false;
            try {
                /* Network Information API: Chromium and Android only, hence untyped. */
                saveData = /** @type {any} */ (navigator).connection?.saveData === true;
            } catch (_) { saveData = false; }
            if (saveData) return;
            /* The avatar picker's faces are the same summoners (the one
               roster, summoner-roster.js): sampled behind the picker's own
               pages, one at a time, while this lobby is still the screen. */
            void warmDeckSelection({ statuses }).then(() => idle(whileLobbyIdle('Avatar faces', () => {
                void AvatarPicker?.warmForLobby?.({ stillIdle: () => lobbyIsIdle() });
            }, idle)));
        }, idle)));
    }

    /* A DECK OR A SUMMONER CHANGED: what the lobby warmed is warmed again.
       The owner (28 September 2026): "the entire game is dynamic ... the
       decks, the summoners ... If something changes, you should update the
       preloaded things". The picker drops what the change made stale the
       moment it is announced (a deck made, changed or deleted; a summoner
       made, confirmed or deleted); this reads it again at the next idle
       moment, once for a burst of changes, and only for a lobby that is
       still the screen. */
    const REWARM_AFTER_CHANGE_MS = 1500;
    let rewarmTimer = null;
    function rewarmAfterChange(event) {
        if (event?.type === 'moonfall:holdings-changed' && event.detail?.source !== 'moon-dream') return;
        clearTimeout(rewarmTimer);
        rewarmTimer = setTimeout(() => {
            rewarmTimer = null;
            // The battle statuses did not change with a deck or a summoner.
            warmDeckSelectionWhenIdle({ statuses: false });
        }, REWARM_AFTER_CHANGE_MS);
    }
    let rewarmWatched = false;
    function rewarmOnChanges() {
        if (rewarmWatched) return;
        rewarmWatched = true;
        ['moonfall:decks-changed', 'moonfall:summoners-changed', 'moonfall:holdings-changed'].forEach((type) => {
            window.addEventListener(type, rewarmAfterChange);
        });
    }

    function bindDeckStartButton(button, {
        prepare = async () => ({}),
        start,
        labelKey,
        fallbackLabel,
        onError,
        entersGame = false,
    }) {
        if (!button) return;
        bindBattleAudioWarmup(button);
        bindDeckSelectionWarmup(button);
        button.addEventListener('click', async () => {
            if (activeDeckStartButtons.has(button)) return;
            prewarmBattleAudio();
            activeDeckStartButtons.add(button);
            // Both reads start now, together, if intent has not already.
            const statuses = warmBattleStatuses();
            let transitionToken = null;
            try {
                const context = await prepare();
                if (context === null) return;

                const choose = DeckSelector?.choose;
                if (typeof choose !== 'function') {
                    console.error('[Lobby] DeckSelector.choose is unavailable.');
                    alert(uiText('deckSelector.unavailable', 'Deck selection is unavailable. Please reload the page and try again.'));
                    return;
                }

                const choice = await choose({
                    ...(context.pickerOptions || {}),
                    mangaStatus: battleStatusFor(statuses.manga),
                    lucidStatus: battleStatusFor(statuses.lucid),
                    returnFocusTo: button,
                });
                if (choice?.kind !== 'selected') return;

                /* The same choice is started again for as long as the start
                   never reaches the server and the player asks for another
                   try (playerRetriesUnreachedStart). */
                for (;;) {
                    if (entersGame) {
                        transitionToken = beginGameTransition({ deck: choice.deck });
                        if (!transitionToken) return;
                    }
                    const result = await runGuardedLobbyAction(
                        button,
                        () => start(choice.deck, context, choice.includeManga, choice.lucid),
                        labelKey,
                        fallbackLabel,
                    );
                    if (isIndeterminateGameStart(result)) {
                        awaitIndeterminateGameStart(transitionToken, result);
                        transitionToken = null;
                    } else if (gameStartFailed(result)) {
                        const retired = transitionToken
                            ? cancelGameTransition(transitionToken)
                            : false;
                        transitionToken = null;
                        if (retired) button.focus();
                        if (await playerRetriesUnreachedStart(result)) continue;
                    } else if (transitionToken) {
                        // A success acknowledgement is sent only after the server
                        // has activated the match and emitted gameStarted. If its
                        // token is still owned, never re-enable lobby actions over
                        // that live game: move to a fatal game recovery surface.
                        failClosedStartedGame(transitionToken, result);
                        transitionToken = null;
                    }
                    break;
                }
            } catch (err) {
                const retired = transitionToken
                    ? cancelGameTransition(transitionToken)
                    : false;
                transitionToken = null;
                const handled = typeof onError === 'function' && onError(err) === true;
                if (!handled) console.error('[Lobby] deck-start action failed:', err);
                if (retired && button.isConnected) button.focus();
            } finally {
                activeDeckStartButtons.delete(button);
            }
        });
    }

    function roomCheckFailureMessage(error) {
        if (error?.code === 'same_account_not_allowed') {
            return uiText(
                'lobby.sameAccountNotAllowed',
                'You cannot join your own multiplayer room. Use a different account for the second player.'
            );
        }
        if (error?.code === 'game_full') {
            return uiText('lobby.roomFull', 'This room is already full.');
        }
        if (error?.message === 'Authentication required.') return null;
        if (error?.message === 'Please sign in again to play.') {
            return uiText('auth.signInAgainToPlay', 'Please sign in again to play.');
        }
        if (error?.message === 'Room not found') {
            return uiText('lobby.validRoomCode', 'Please enter a valid room code.');
        }
        return uiText('lobby.roomCheckFailed', 'Failed to check room.');
    }

    function isExpectedRoomCheckFailure(error) {
        if ([
            'same_account_not_allowed',
            'game_full',
            'timeout',
            'no_response',
        ].includes(error?.code)) return true;
        return [
            'Authentication required.',
            'Please sign in again to play.',
            'Room not found',
            'Failed to check room.',
        ].includes(error?.message);
    }

    function initEventListeners() {
        bindInGameReturnControl();
        syncUtilityPlacement();
        // Utility reparenting is visual chrome, so it follows the native resize
        // synchronously. The debounced platform event remains for expensive
        // pagination/refit work but must not leave Discord/Feedback detached for
        // a visible frame after an orientation or responsive-size change.
        window.addEventListener('resize', syncUtilityPlacement, { passive: true });
        window.addEventListener('orientationchange', syncUtilityPlacement, { passive: true });
        window.addEventListener('moonfall:viewport-change', syncUtilityPlacement);

        /* The tiles that lead to the picker are intent too: the Adventure tile
           opens the sheet whose button opens it, and on a phone its tap is the
           only early signal there is. The deck-start buttons bind their own. */
        bindDeckSelectionWarmup(elements.adventureTile);
        bindDeckSelectionWarmup(elements.friendsTile);
        /* New information about the statuses: a purchase or voucher (plan),
           a checkout return (Moonlight), a new identity. */
        [
            'moonfall:subscription-changed',
            'moonfall:moonlight-stale',
            'moonfall:moonlight-changed',
            'moonfall:convex-auth-changed',
        ].forEach((name) => window.addEventListener(name, forgetBattleStatuses));

        bindDeckStartButton(elements.createRoomBtn, {
            start: (deck, _context, includeManga, lucid) => {
                const options = battleOptions(includeManga, lucid);
                return options
                    ? SocketHandler.createRoom(deck, options)
                    : SocketHandler.createRoom(deck);
            },
            labelKey: 'lobby.creatingRoom',
            fallbackLabel: 'Creating room…',
        });

        elements.copyCodeBtn.addEventListener('click', copyRoomCode);
        if (offersInviteShareSheet()) {
            dressInviteShareButton(elements.copyLinkBtn);
            elements.copyLinkBtn.addEventListener('click', shareInviteLink);
        } else {
            elements.copyLinkBtn.addEventListener('click', copyInviteLink);
        }

        bindDeckStartButton(elements.joinRoomBtn, {
            prepare: async () => {
                const code = elements.roomCodeInput.value.trim();
                if (!code) return null;
                const info = await SocketHandler.getRoomInfo(code);
                if (info?.exists !== true) {
                    const error = new Error(
                        info?.ok === false ? 'Failed to check room.' : 'Room not found'
                    );
                    if (info?.code) error.code = info.code;
                    throw error;
                }
                if (Number(info?.playerCount) >= 2) {
                    const error = new Error('Game is full');
                    error.code = 'game_full';
                    throw error;
                }
                const excludedCharacter = info?.hostCharacter || null;
                if (excludedCharacter) {
                    console.log('[Lobby] Room host uses character:', excludedCharacter);
                }
                return { code, pickerOptions: { excludedCharacter } };
            },
            start: (deck, { code }, includeManga, lucid) => {
                const options = battleOptions(includeManga, lucid);
                return options
                    ? SocketHandler.joinRoom(code, deck, options)
                    : SocketHandler.joinRoom(code, deck);
            },
            labelKey: 'lobby.joiningRoom',
            fallbackLabel: 'Joining room…',
            onError: (error) => {
                const message = roomCheckFailureMessage(error);
                if (message) alert(message);
                return isExpectedRoomCheckFailure(error);
            },
            entersGame: true,
        });

        bindDeckStartButton(elements.soloAdventureBtn, {
            start: (deck, _context, includeManga, lucid) => {
                const options = battleOptions(includeManga, lucid);
                return options
                    ? SocketHandler.startSoloGame(deck, options)
                    : SocketHandler.startSoloGame(deck);
            },
            labelKey: 'lobby.startingSolo',
            fallbackLabel: 'Starting Solo Adventure…',
            entersGame: true,
        });

        guardLobbyButton(elements.tutorialBtn, async () => {
            for (;;) {
                const transitionToken = beginGameTransition();
                if (!transitionToken) return false;
                let started;
                try {
                    const resumeToken = MoonfallGuest?.getResumeToken?.() || null;
                    started = await SocketHandler.startTutorial(resumeToken ? { resumeToken } : {});
                    if (isIndeterminateGameStart(started)) {
                        awaitIndeterminateGameStart(transitionToken, started);
                        return started;
                    }
                    if (!gameStartFailed(started)) {
                        failClosedStartedGame(transitionToken, started);
                        return started;
                    }
                    cancelGameTransition(transitionToken);
                    updateTutorialButtonMode();
                } catch (error) {
                    cancelGameTransition(transitionToken);
                    throw error;
                }
                if (!(await playerRetriesUnreachedStart(started))) return started;
            }
        }, 'lobby.startingTutorial', 'Starting Tutorial…', 3000);
        bindBattleAudioWarmup(elements.tutorialBtn);

        // Collection button
        if (elements.openFoundryBtn) {
            /* Entry prefetch on a real intent signal. The Foundry surface is
               presented once its first view is ready (bounded), and the
               inventory — which carries `firstDiscovered` — and the prices are
               the reads it waits on. Warming them on hover/focus/touch means
               the press usually finds them in hand; a cold warm simply loads
               as before. Repeat intent reuses the warm in flight. The lobby's
               idle warm takes the prices only (warmAtIdle): the inventory is
               the player's whole library, read on intent and never before. */
            elements.openFoundryBtn.addEventListener('pointerenter', warmFoundry);
            elements.openFoundryBtn.addEventListener('focus', warmFoundry);
            elements.openFoundryBtn.addEventListener('touchstart', warmFoundry, { passive: true });
            elements.openFoundryBtn.addEventListener('click', openFoundrySurface);
        }

        if (elements.openCollectionBtn) {
            // Hover/focus/first touch is a strong open signal — refresh the
            // first collection page before the click (docs/asset-loading.md
            // law 3). A phone has no hover: its touch lands ~100 ms early.
            const prefetchCollection = () => Collection?.prefetchFirstPage?.();
            elements.openCollectionBtn.addEventListener('pointerenter', prefetchCollection);
            elements.openCollectionBtn.addEventListener('focus', prefetchCollection);
            elements.openCollectionBtn.addEventListener('touchstart', prefetchCollection, { passive: true });
            elements.openCollectionBtn.addEventListener('click', () => {
                if (Collection) {
                    // The tile answers the press while the Collection is
                    // finished off-screen (Collection.open holds until then).
                    Collection.open('cards', { opener: elements.openCollectionBtn });
                }
            });
        }

        // The Community row copies the room link on its way out when there is
        // a room to share; the waiting room's line posts the room itself.
        document.getElementById('pvp-discord-link')?.addEventListener('click', handleDiscordLinkClick);
        document.getElementById('room-discord-link')?.addEventListener('click', handleRoomDiscordLinkClick);
        // Before a room exists there is nothing to copy: the line only opens Discord.
        document.getElementById('friends-discord-link')?.addEventListener('click', () => {
            DiscordLinks.noteOpened('friends');
        });

        elements.goBackBtn.addEventListener('click', resetLobby);

        const entryUrl = new URL(window.location.href);
        if (entryUrl.searchParams.get('surface') === 'foundry') {
            /* The button path warms the inventory on hover/focus/touch; this
               deep link (`/play?surface=foundry`, which /foundry redirects to)
               went straight in cold. Inventory is the ONLY source of the
               first-discoverer flag, so arriving cold is exactly why those
               ribbons popped in about a second after the chips. */
            warmFoundry();
            openFoundrySurface(null, entryUrl.searchParams.get('tab') || 'moon-fuse');
        }
    }

    function handleDiscordLinkClick(event) {
        DiscordLinks.noteOpened(event?.currentTarget?.dataset?.discordSurface);
        const roomId = GameState.get().roomId;
        if (!roomId) return; // Nothing to copy outside a room — just open Discord.
        /* Say it only once it is true. The toast used to fire before the
           ladder answered, so a refused copy still announced "Invite link
           copied" while Discord opened on an empty clipboard; the refusal
           is already flashed on the copy button by copyInviteLink(), and a
           share sheet carries its own Copy action, so neither gets a claim. */
        copyInviteLink().then((outcome) => {
            if (!outcome?.ok || outcome.method === 'share') return;
            showLobbyToast(uiText('lobby.discordLinkCopied', 'Invite link copied — paste it in Discord'));
        });
    }

    /* The waiting room's line. While the link opens #looking-for-players,
       the server posts the room there for the host and takes the post down
       when the room fills or closes. The room link is still copied, quietly
       (no share sheet over Discord), for when the post cannot be made: the
       feature is off, today's allowance is spent, Discord refused. The toast
       says which of the two happened, once it is true. */
    function handleRoomDiscordLinkClick(event) {
        DiscordLinks.noteOpened(event?.currentTarget?.dataset?.discordSurface);
        const roomId = GameState.get().roomId;
        if (!roomId) return;
        const copied = CopyText.copy(buildInviteLink(), { allowShareFallback: false })
            .catch(() => ({ ok: false, method: null }));
        const posted = Promise.resolve()
            .then(() => SocketHandler.postRoomToDiscord(roomId))
            .catch(() => ({ ok: false }));
        Promise.all([posted, copied]).then(([post, copy]) => {
            if (GameState.get().roomId !== roomId) return;
            if (post?.ok) {
                showLobbyToast(uiText('lobby.roomPostedToDiscord', 'Your room is posted in #looking-for-players'));
            } else if (copy?.ok) {
                showLobbyToast(uiText('lobby.discordLinkCopied', 'Invite link copied — paste it in Discord'));
            }
        });
    }

    function uiText(key, fallback) {
        if (typeof i18n?.t === 'function') {
            const v = i18n.t(key);
            if (v && v !== key) return v;
        }
        return fallback;
    }

    let activeToastTimers = null;
    function showLobbyToast(message) {
        if (!message) return;
        if (activeToastTimers) {
            clearTimeout(activeToastTimers.exit);
            clearTimeout(activeToastTimers.remove);
            activeToastTimers.toast.remove();
        }
        const toast = document.createElement('div');
        toast.className = 'lobby-toast';
        toast.setAttribute('role', 'status');
        toast.textContent = message;
        document.body.appendChild(toast);
        const exit = setTimeout(() => toast.classList.add('lobby-toast-exit'), 2500);
        const remove = setTimeout(() => {
            toast.remove();
            if (activeToastTimers && activeToastTimers.toast === toast) activeToastTimers = null;
        }, 3000);
        activeToastTimers = { toast, exit, remove };
    }

    function isPaidStatus(value) {
        if (typeof value === 'boolean') return value;
        return Boolean(value && typeof value === 'object' && value.isPaid === true);
    }

    /** The account's last Manga Pass answer (true when paid), or null before one. */
    let lastPaidAnswer = null;

    async function updateTutorialButtonMode() {
        if (!elements.tutorialBtn) return;
        const resumeToken = MoonfallGuest?.getResumeToken?.() || null;
        const hasResume = !!resumeToken;
        // Under the gold Start Adventure button, the fresh tutorial is the
        // quiet second line the reference wrote: "Play the tutorial first".
        // Once the player has finished it, that advice is no longer true.
        let key = 'hub.tutorialFirst';
        let fallback = 'Play the tutorial first';
        if (hasResume) {
            key = 'lobby.continueTutorial';
            fallback = 'Continue Tutorial';
        } else if (Tutorial?.hasCompleted?.() === true) {
            key = 'hub.tutorialReplay';
            fallback = 'Replay the tutorial';
        }
        elements.tutorialBtn.setAttribute('data-i18n', key);
        elements.tutorialBtn.textContent = uiText(key, fallback);

        const note = elements.tutorialResumeMangaNote;
        if (!note) return;
        const noteText = uiText(
            'lobby.tutorialResumeMangaNote',
            'Manga Pass may enable manga for the continued duel, subject to current availability and limits.'
        );
        if (note.textContent !== noteText) note.textContent = noteText;
        if (!hasResume) {
            note.hidden = true;
            return;
        }
        /* The last paid answer decides the note until the new one lands: it
           used to be un-hidden first and hidden again a round trip later, so
           a Manga Pass holder saw it pop in and out of the open sheet. */
        if (lastPaidAnswer !== null) note.hidden = lastPaidAnswer;

        try {
            const paidStatus = await MoonfallCloud?.users?.isPaid?.();
            if (MoonfallGuest?.getResumeToken?.() !== resumeToken) return;
            lastPaidAnswer = isPaidStatus(paidStatus);
            note.hidden = lastPaidAnswer;
        } catch (err) {
            console.warn('[Lobby] Could not resolve Manga Pass status for tutorial continuation', err);
            if (lastPaidAnswer === null) note.hidden = false;
        }
    }

    /**
     * The Foundry's first view, read ahead of the press on the tile's intent
     * (and by the `?surface=foundry` deep link as it opens): this account's
     * inventory in this language, and the fusion prices. Best effort and
     * memoized in FoundryInventoryWarm, so repeat intent, the lobby's idle
     * price warm and the surface share one request each.
     */
    function warmFoundry() {
        FoundryInventoryWarm?.warmUp?.(
            Firebase?.getUser?.() || null,
            i18n?.getLanguage?.() || 'en',
        );
        FoundryInventoryWarm?.warmConfig?.();
    }

    function openFoundrySurface(event, tab = null) {
        if (event) event.preventDefault();
        const normalizedTab = tab === null ? null : (tab === 'moon-dream' ? 'moon-dream' : 'moon-fuse');
        if (FoundrySurface?.open) {
            const opened = FoundrySurface.open(normalizedTab === null ? {} : { tab: normalizedTab });
            /* THE PRESS IS ANSWERED ON THE TILE. The surface is presented only
               once its first view is ready (bounded at 4 s), so a press it has
               to wait for shows the tile busy — the shared click guard — until
               the Foundry appears whole. A first view already in hand presents
               inside open() and the guard releases before anything paints. */
            const tile = event?.currentTarget;
            if (opened && tile && tile === elements.openFoundryBtn && MoonfallClickGuard?.guard) {
                /* THE RING IS THE PRESS'S ONE LOADING STATE. Past the grace
                   it is all the player sees of a Foundry being prepared, so
                   it carries the mark every organized loading state carries
                   (`data-loading`, as surface-entry-hold.js's line does), for
                   exactly as long as this hold keeps it turning. A tile
                   already held keeps its own hold's mark. */
                const ring = MoonfallClickGuard.isPending?.(tile)
                    ? null
                    : tile.querySelector('.hub-tile-busy');
                if (ring) ring.dataset.loading = 'true';
                void MoonfallClickGuard.guard(tile, () => FoundrySurface.whenPresented?.())
                    .catch(() => undefined)
                    .then(() => { if (ring) delete ring.dataset.loading; });
            }
            return;
        }
        window.location.href = `/play?surface=foundry&tab=${normalizedTab || 'moon-fuse'}`;
    }

    function closeFoundrySurface(options) {
        return FoundrySurface?.close?.(options);
    }

    /**
     * The tap's answer, on the button itself. "seems to not work on ios" was
     * two defects in one report: the old fallback chain genuinely could not
     * copy on iOS, and even a SUCCESSFUL copy showed zero pixels of feedback,
     * which is indistinguishable from failure. The share sheet is its own
     * feedback, so that outcome flashes nothing.
     */
    const copyFlashTimers = new WeakMap();
    function flashCopyOutcome(button, outcome) {
        if (!button || outcome?.method === 'share') return;
        const previous = copyFlashTimers.get(button);
        if (previous) {
            clearTimeout(previous.timer);
        } else {
            copyFlashTimers.set(button, { original: button.textContent });
        }
        const entry = copyFlashTimers.get(button);
        button.textContent = outcome?.ok
            ? uiText('lobby.copied', 'Copied!')
            : uiText('lobby.copyFailed', "Couldn't copy");
        entry.timer = setTimeout(() => {
            button.textContent = entry.original;
            copyFlashTimers.delete(button);
        }, 1600);
    }

    /**
     * Copy room code to clipboard
     */
    function copyRoomCode() {
        CopyText.copy(GameState.get().roomId)
            .then((outcome) => flashCopyOutcome(elements.copyCodeBtn, outcome))
            .catch(err => console.error('Could not copy text: ', err));
    }

    /**
     * THE invite link: `/play?room=CODE` on this deployment's origin, and on
     * moonfall.io when the game is framed by itch.io. It is the one URL every
     * hand-off below carries -- copied, shared, or sent to Discord -- because
     * it is the URL the server unfurls into the invitation card
     * (docs/room-invite-card.md): the host's name, their summoner's portrait
     * and "accept the match", drawn from Open Graph tags on the link itself.
     * A chat that receives this exact link shows that card; a link built any
     * other way (a `lang`, a hash, a path the server does not know) shows a
     * bare URL that reads like spam. Nothing is appended here on purpose.
     */
    function buildInviteLink() {
        const roomId = GameState.get().roomId;
        if (!roomId) return '';
        let baseUrl;
        // Check if running on itch.io (itch.zone is used for the iframe content)
        if (window.location.hostname.includes('itch.zone') || window.location.hostname.includes('itch.io')) {
            baseUrl = 'https://moonfall.io/play';
        } else {
            // Include pathname to support deployments under a subpath (e.g., /Moonfall)
            baseUrl = `${window.location.origin}${window.location.pathname}`;
        }
        return `${baseUrl}?room=${roomId}`;
    }

    /**
     * Copy invite link to clipboard
     */
    function copyInviteLink() {
        const inviteLink = buildInviteLink();
        return CopyText.copy(inviteLink, { shareTitle: 'Moonfall' })
            .then((outcome) => {
                if (elements.copyLinkBtn?.dataset.inviteMode === 'share') {
                    /* The control beside the code reads "Invite a Friend" and
                       only the Discord button copies through here, so a verdict
                       flashed on it would answer a tap it never received. The
                       Discord path toasts its success; a refusal is toasted
                       here so it is never silent. */
                    if (!outcome?.ok) showLobbyToast(uiText('lobby.copyFailed', "Couldn't copy"));
                } else {
                    flashCopyOutcome(elements.copyLinkBtn, outcome);
                }
                if (outcome?.ok && MoonfallAnalytics) MoonfallAnalytics.capture('invite_copied');
                return outcome;
            })
            .catch((err) => {
                console.error('Could not copy text: ', err);
                return { ok: false, method: null };
            });
    }

    /**
     * Whether the created-room row offers the system share sheet instead of
     * the clipboard. On a phone the sheet IS the invitation: one tap puts the
     * link in front of the friend's name in Messages, WhatsApp, Telegram,
     * Discord -- wherever they already are -- and every one of those draws
     * the invitation card from the link's Open Graph tags, exactly as the
     * copied link would unfurl. Offered only where the device is touch-first
     * (a phone/tablet user agent, or a coarse primary pointer) AND the engine
     * implements navigator.share. A desktop keeps Copy Invite Link even where
     * share exists (Safari on a Mac, Chrome on Windows): there the sheet is a
     * detour from a chat window that already has the clipboard, and this
     * host's Linux engines have no sheet at all.
     */
    function offersInviteShareSheet() {
        if (typeof navigator.share !== 'function') return false;
        /* No up-front permissions-policy read: Chromium answers
           `allowsFeature('web-share')` false wherever the sheet is not built
           in (every Linux seat in the matrix reported Copy Invite Link), and
           whether Android says true cannot be checked from here. A host that
           withheld the policy (an itch.io frame) refuses at the tap instead,
           and that refusal is answered by the clipboard fallback and a toast. */
        return Boolean(Platform?.isMobileUA?.() || Platform?.isTouchPrimary?.());
    }

    /** The same control, wearing the sheet's label. `data-i18n` so a language
        switch relabels it with the rest of the lobby. */
    function dressInviteShareButton(button) {
        if (!button) return;
        button.dataset.inviteMode = 'share';
        button.setAttribute('data-i18n', 'lobby.inviteFriend');
        button.textContent = uiText('lobby.inviteFriend', 'Invite a Friend');
    }

    /**
     * Open the share sheet on the invite link.
     *
     * The link travels as `url`, never folded into `text`. iOS hands a URL
     * item to the receiving app as a link, which is what makes Messages,
     * WhatsApp and Mail draw the invitation card; a URL buried in text is
     * previewed by some of them and printed bare by others. Android merges
     * `text` and `url` into one intent string with the link last, and the
     * text ends in a full stop so every chat app's link detector cuts the URL
     * cleanly. `title` is the mail subject on both and ignored by chats.
     *
     * `navigator.share` is called synchronously from the tap: both platforms
     * tie it to the gesture's transient activation, and an `await` before it
     * would lose the gesture on WebKit.
     *
     * Three outcomes, each said in the one way the player can see:
     * - shared: the sheet is its own feedback, the label does not move;
     * - dismissed (AbortError): nothing happened, nothing is said;
     * - refused (anything else -- WebKit's "share() is already in progress"
     *   after a sheet torn down by an app switch, a missing activation, an
     *   engine that lies about the API): the link goes to the clipboard
     *   through the same ladder Copy Invite Link uses, the button flashes the
     *   clipboard's verdict and a toast explains why it did that instead.
     *   Swallowing the refusal was the dead button of 2026-09-15.
     */
    function shareInviteLink() {
        const inviteLink = buildInviteLink();
        if (!inviteLink) return Promise.resolve({ ok: false, method: null });
        const payload = {
            title: uiText('lobby.inviteShareTitle', 'Join my Moonfall match'),
            text: uiText('lobby.inviteShareText', 'Play a Moonfall match with me. The link takes you straight to my room.'),
            url: inviteLink,
        };
        let sheet;
        try {
            sheet = navigator.share(payload);
        } catch (error) {
            sheet = Promise.reject(error);
        }
        return Promise.resolve(sheet).then(
            () => {
                if (MoonfallAnalytics) MoonfallAnalytics.capture('invite_shared');
                return { ok: true, method: 'share' };
            },
            (error) => {
                const cancelled = error?.name === 'AbortError';
                if (MoonfallAnalytics) {
                    MoonfallAnalytics.capture('invite_share_failed', { reason: cancelled ? 'cancelled' : 'unavailable' });
                }
                if (cancelled) return { ok: false, method: 'share', cancelled: true };
                return CopyText.copy(inviteLink, { allowShareFallback: false })
                    .catch(() => ({ ok: false, method: null }))
                    .then((outcome) => {
                        flashCopyOutcome(elements.copyLinkBtn, outcome);
                        if (outcome?.ok) {
                            if (MoonfallAnalytics) MoonfallAnalytics.capture('invite_copied');
                            showLobbyToast(uiText('lobby.inviteShareUnavailableCopied', 'Sharing is unavailable right now. Invite link copied instead.'));
                        } else {
                            showLobbyToast(uiText('lobby.inviteShareUnavailable', 'Sharing is unavailable right now. Copy the room code instead.'));
                        }
                        return outcome;
                    });
            },
        );
    }


    /**
     * Reset the lobby to its initial state
     */
    function setRoomActionsVisible(visible) {
        if (elements.roomActions) elements.roomActions.style.display = visible ? '' : 'none';
    }

    function renderPvpRoomState(state, data = {}) {
        const active = state === 'created' || state === 'joined';
        const created = state === 'created';
        const joined = state === 'joined';

        elements.pvpGame.dataset.roomState = active ? state : 'idle';
        elements.buttonRow.style.display = active ? 'none' : '';
        setRoomActionsVisible(!active);
        elements.createRoomBtn.style.display = active ? 'none' : '';
        elements.joinRoomBtn.style.display = active ? 'none' : '';
        elements.joinRoomBar.style.display = active ? 'none' : 'block';
        if (elements.roomStateHead) elements.roomStateHead.style.display = active ? 'flex' : 'none';
        elements.roomInfo.style.display = created ? 'flex' : 'none';
        elements.waitingHost.style.display = joined ? 'flex' : 'none';
        elements.waitingHost.classList.toggle('waiting-host--searching', joined);
        elements.roomCodeSpan.textContent = active ? String(data.roomId || '') : '';
        elements.lobbyView.classList.toggle('show-back-button', active);
        syncUtilityPlacement();
        /* A live room lives in the Friends sheet. A Create Room press, a
           resumed room and an invite link all land here, so the sheet opens
           itself (never stealing focus from a field the player is typing in)
           and locks — Go Back is the exit. See hub-sheets.js. */
        if (active) HubSheets?.open?.('friends', { focus: false });
        HubSheets?.syncLock?.();
    }

    function tearDownInGameUI() {
        try { SoundEffects?.stopAll?.(); } catch (_) { }
        try { Feedback?.close?.({ restoreFocus: false }); } catch (_) { }
        try { Mulligan?.hide?.({ immediate: true }); } catch (_) { }
        try {
            DeckSelector?.close?.({
                outcome: { kind: 'cancelled' },
                restoreFocus: false,
            });
        } catch (_) { }
        try { Mulligan?.hideWaitingBanner?.(); } catch (_) { }
        try { ActionIntent?.clear?.(); } catch (_) { }
        try { SpellAnnounce?.dismiss?.(); } catch (_) { }
        try { SocketHandler?.clearGameNotifications?.(); } catch (_) { }
        try { Indicators?.clearStatusIndicators?.(); } catch (_) { }
        /* The per-match STORES, not just the overlays. Leaving mid-game via
           Return to Lobby used to skip these (only the game-END ritual in
           game-end.js ran them), so the story feed kept every entry, manga
           wait and timer across matches: the next battle started with the
           previous game's manga and turns already in its lane, and a late
           manga decode could paint on top of the lobby itself. Keep this
           list in sync with cleanUpGameState() in game-end.js. */
        try { StoryFeed?.reset?.(); } catch (_) { }
        try { TurnBanner?.reset?.(); } catch (_) { }
        try { CardDrag?.reset?.(); } catch (_) { }
        try { Cards?.resetMatchTracking?.(); } catch (_) { }
        // The match's art plan stops with it (asset-preloader.ts).
        try { AssetPreloader?.endMatchWarm?.(); } catch (_) { }
        try { ThinkingIndicator?.reset?.(); } catch (_) { }
        try { TurnTimer?.reset?.(); } catch (_) { }

        document.querySelectorAll('.game-notification').forEach((toast) => {
            try { toast.remove(); } catch (_) { }
        });

        // A catalog glide's preview being left gave the id up (hover-handoff.js,
        // "ONE ELEMENT NAMES THE PREVIEW"); the by-id sweep below cannot see it.
        try { HoverHandoff?.releaseHandedOff?.(); } catch (_) { }
        ['game-over-popup', 'fatal-error-popup', 'card-hover-preview'].forEach((id) => {
            const el = document.getElementById(id);
            if (el && el.parentNode) el.parentNode.removeChild(el);
        });

        document.body.classList.remove('action-intent-active');
        /* This block detaches the result popup NODE without going through
           game-end's removeResultModal(), which is what normally clears the
           marker — so `game-result-active` outlived the modal on every path
           that lands here directly. It already left `#skip-tutorial-btn`
           hidden in the lobby, and now it matters more: feedback.js and
           deck-selector.js read this marker as "a terminal result screen is
           up", so a leaked one would make them openable during the NEXT live
           duel — the precise thing their battle guard exists to prevent. */
        document.body.classList.remove('game-result-active');
    }

    function resetLobby() {
        // Reset UI elements
        try { SocketHandler?.cancelLobbyRoomAdmission?.(); } catch (_) { }
        // Let the responsive stylesheet restore the panels' flex contract.
        // An inline `block` here survives every breakpoint and breaks the
        // equal-height/mobile auto-margin layout after Go Back.
        elements.pvpGame.style.removeProperty('display');
        elements.singlePlayerPanel?.style.removeProperty('display');
        renderPvpRoomState('idle');

        // End tutorial if active
        try { if (Tutorial?.isActive?.()) { Tutorial.end(); } } catch (_) { }

        // Reset form elements
        elements.roomCodeInput.value = '';

        // Ensure any action overlay is cleared before leaving room/state reset
        try { if (GameState && typeof GameState.setActionInProgress === 'function') { GameState.setActionInProgress(false); } } catch (_) { }


        // Disconnect from current room (must pass explicit roomId BEFORE reset)
        const currentRoomId = GameState.get().roomId;
        if (currentRoomId) {
            SocketHandler.leaveRoom(currentRoomId);
        }

        // Clear URL query string (e.g., ?room=...) to avoid stale room codes on lobby
        try {
            if (window.history && typeof window.history.replaceState === 'function') {
                const newUrl = window.location.pathname + (window.location.hash || '');
                window.history.replaceState({}, '', newUrl);
            }
        } catch (_) { }

        try { MoonfallClickGuard?.forceRelease?.(elements.tutorialBtn); } catch (_) { }

        // Reset game state
        GameState.reset();
        updateTutorialButtonMode();
    }

    /**
     * Load user's saved decks into the deck selectors
     */
    /**
     * Load user's saved decks (No-op in new UI, handled by DeckSelector)
     */
    async function loadUserDecks() {
        // Legacy function retained for API compatibility
        // Deck selection is now handled via DeckSelector popup
    }

    // Battle-entry veil: a brief dark cover that fades to reveal the
    // battlefield. pointer-events: none in CSS guarantees it can never block
    // input. Re-triggers restart the fade instead of letting the previous
    // timer strip the class mid-animation.
    let battleVeilTimer = null;

    function playBattleVeil() {
        const gameView = elements.gameView;
        if (!gameView) return;
        if (battleVeilTimer) clearTimeout(battleVeilTimer);
        if (gameView.classList.contains('battle-enter')) {
            // Restart the CSS animation from its opaque frame.
            gameView.classList.remove('battle-enter');
            void gameView.offsetWidth;
        }
        gameView.classList.add('battle-enter');
        battleVeilTimer = setTimeout(() => {
            gameView.classList.remove('battle-enter');
            battleVeilTimer = null;
        }, 700);
    }

    // Public API
    return {
        /**
         * Initialize the lobby module
         */
        init: function () {
            // The hub's sheets (Adventure, Friends, Community) bind their own
            // openers; the room card inside the Friends sheet is still ours.
            HubSheets?.init?.();
            initEventListeners();
            updateTutorialButtonMode();
            warmDeckSelectionWhenIdle();
            rewarmOnChanges();
        },

        bindInGameReturnControl,
        syncReturnControlLabel,
        /* The lobby owns the only toast surface a signed-in player sees before
           a match, so a refused start is reported through it rather than
           through a native dialog. Called by socket-handler's
           surfaceIndeterminateStart. */
        showStartRefusal: showLobbyToast,
        // One short line for the lobby's other modules (feedback, Discord pings).
        showToast: showLobbyToast,
        // Referrals drops the Invite pill into the same top bar; it re-asserts
        // the order afterwards so Discord keeps sitting right of Invite.
        syncUtilityPlacement,
        runGameAdmission,

        /**
         * Load user decks (call after auth)
         */
        loadDecks: loadUserDecks,

        /**
         * Handle room creation event
         * @param {Object} data - Room creation data
         */
        onRoomCreated: function (data) {
            renderPvpRoomState('created', data);
        },

        /**
         * Handle room joined event
         * @param {Object} data - Room joined data
         */
        onRoomJoined: function (data) {
            renderPvpRoomState('joined', data);
        },

        /**
         * Update the players status display
         * @param {Array} players - Array of player objects
         */
        updatePlayersStatus: function () {
            // No-op: Game Status panel removed.
        },

        /**
         * Switch from lobby view to game view.
         * @param {Object} [options]
         * @param {boolean} [options.veil=true] - Play the battle-entry veil.
         *   Mulligan entries pass false: the mulligan overlay has its own
         *   fade-in, and the veil instead fires at the mulligan → battlefield
         *   handoff (see playBattleVeil).
         * @param {{presented: Promise, capMs: number}|null} [options.arrival] -
         *   The match loading screen's pending arrival (MatchLoading.arrive):
         *   a boot veil then stays over the board until it is whole.
         */
        /**
         * Play the battle-entry veil on demand (e.g. at the mulligan →
         * battlefield handoff, over an already-rendered board).
         */
        playBattleVeil: playBattleVeil,

        switchToGameView: function ({ veil = true, arrival = null } = {}) {
            console.log('[Lobby] Switching to Game View (Hiding Lobby)');
            /* A board is a destination: a boot that was waiting for it (a
               mid-match refresh, the guest tutorial) shows it from under the
               loading veil, never from over a lobby it painted first — and
               not before it is whole. Dropped here, the veil lifted in the task
               that built the board, before a single picture on it had loaded
               (the first-visit tutorial's card backs and pile canvases); with
               an arrival it waits for the match loading screen's word,
               capped. */
            if (arrival?.presented) {
                BootVeil.arrive('game', arrival.presented, arrival.capMs);
            } else {
                BootVeil.drop('game');
            }
            // The cues follow the board's first screen (or come now when there
            // is no arrival to wait for).
            openBattleAudioGate(arrival?.presented);
            prewarmBattleAudio();
            // Keep the pending gate through synchronous surface retirement.
            // `game-active` is established before it is removed, so there is
            // no frame in which lobby controls can reopen over the handoff.
            /* A resume of the match already on screen (a tab return, a
               reconnect) enters a view the player never left: its entry-only
               work — the focus move — would pull keyboard focus out of a kept
               mulligan or action panel onto Return to Lobby. */
            const gameViewWasShown = document.body.classList.contains('game-active')
                && elements.gameView.style.display === 'flex';
            closeLobbySurfacesForGame();
            // Match found — stop the "searching" pulse before we leave the lobby.
            elements.waitingHost?.classList.remove('waiting-host--searching');
            elements.lobbyView.style.display = 'none';
            document.body.classList.remove('lobby-active');
            elements.lobbyView.style.setProperty('display', 'none', 'important'); // Force hide
            // Also leave the sign-in card if it is showing. A guest replay boots the
            // board straight from the sign-in screen, so hiding auth here — the single
            // canonical "entering a game" switch — guarantees the board never renders
            // over the auth view, no matter which path started the game. (Signed-in
            // users already have auth-view hidden by proceedToLobby, so this is a
            // harmless no-op for them.)
            const authView = document.getElementById('auth-view');
            if (authView) authView.style.display = 'none';
            elements.gameView.style.display = 'flex';
            // Hide sign-out button during game
            document.body.classList.add('game-active');
            const userControls = document.getElementById('user-controls');
            if (userControls) userControls.style.display = 'none';
            finishGameTransition();

            runOptionalGameTransitionStep(
                'Language selector teardown',
                () => i18n?.hideSelector?.()
            );
            if (veil) {
                runOptionalGameTransitionStep('Battle veil', playBattleVeil);
            }
            /* Kept only while the player still holds a focus somewhere (a
               kept mulligan, an action panel). A rematch removes the result
               dialog that held it, leaving the page body: the new match then
               takes the focus like any other entry. */
            const activeElement = document.activeElement;
            const focusStillHeld = gameViewWasShown
                && !!activeElement
                && activeElement !== document.body
                && activeElement.isConnected;
            if (!focusStillHeld) {
                runOptionalGameTransitionStep(
                    'Game view focus',
                    () => focusViewDestination(elements.gameView, '#return-lobby-btn')
                );
            }
        },

        /**
         * Switch to lobby view after game ends.
         *
         * Single entry point for "I'm leaving the game" — used by both the
         * game-end popup and the tutorial Skip button. Tears down every
         * in-game UI surface (mulligan overlay/timer/banner, action-intent
         * panel, transient toasts, end-game popups) so no module is left
         * ticking in the background after the player returns to the lobby.
         */
        switchToLobbyView: function () {
            /* A resume the server refused lands here while the boot veil is
               still holding for the board; the lobby is the answer now. Only
               the resume's hold is settled: the lobby's own arrival hold
               still decides when the veil lifts (dropping it through that
               hold let the quest rail, the avatar and the Moonlight arrive in
               front of the player). No-ops once the veil is gone. */
            if (typeof BootVeil.settle === 'function') {
                BootVeil.settle('game-resume');
                BootVeil.reveal?.('lobby');
            } else {
                BootVeil.drop('lobby');
            }
            runOptionalGameTransitionStep('Match loading screen', () => MatchLoading?.cancel?.());
            finishGameTransition();
            closeLobbySurfacesForGame({ includeTransientSurfaces: false });
            tearDownInGameUI();

            /* A lobby the player is already looking at (a refused resume
               that never left it) is not entered again: replaying its fade
               blinked the lobby and the account bar out and back in. */
            const lobbyWasShown = !document.body.classList.contains('game-active')
                && elements.gameView.style.display !== 'flex'
                && elements.lobbyView.style.display !== 'none';
            elements.lobbyView.style.display = 'block';
            if (!lobbyWasShown) elements.lobbyView.scrollTop = 0;
            document.body.classList.add('lobby-active');
            elements.gameView.style.display = 'none';
            if (!lobbyWasShown) Effects?.viewEnter?.(elements.lobbyView);
            if (i18n) i18n.showSelector();

            document.body.classList.remove('game-active');
            const userControls = document.getElementById('user-controls');
            if (userControls) {
                userControls.style.display = 'flex';
                // Same crossfade as the view behind it — see main.js.
                if (!lobbyWasShown) Effects?.viewEnter?.(userControls);
            }

            // resetLobby ends the tutorial, leaves the room, and resets GameState.
            resetLobby();

            /* A MATCH MOVED THE MOONLIGHT, and the game server spent it out of
               this client's sight: say so. The chip beside the Stardust (which
               re-reads only when told) used to keep its pre-duel figure until
               a reload. The socket already said it as the result was shown
               (socket-handler.js gameOver), so the chip usually has the new
               figure before the lobby shows; this covers the ways out with no
               result (Exit Match, a refused resume) and what settled after it
               (the last turn's panels). One read, shared with the store's
               below and the picker's (convex-client.js). */
            announceMoonlightMoved();

            /* A match moved the Moonlight the store shows: warm its answers
               again so its next open paints today's figures, not the ones
               from before the match. */
            Store?.warmData?.();

            if (Quests?.refresh) {
                Quests.refresh();
            }
            if (Referrals?.refresh) {
                Promise.resolve(Referrals.refresh()).catch((err) => {
                    console.warn('[Lobby] Failed to refresh referral status:', err);
                });
            }
            const consumeLobbyRewards = Referrals?.consumeLobbyRewards
                || StarterReward?.consumeAndShow;
            if (consumeLobbyRewards) {
                Promise.resolve(consumeLobbyRewards()).catch((err) => {
                    console.warn('[Lobby] Failed to show lobby rewards:', err);
                });
            }
            // Focus moves only onto a lobby the player is arriving at.
            if (!lobbyWasShown) {
                focusViewDestination(
                    elements.lobbyView,
                    '#solo-adventure-btn, #tutorial-btn, button:not([disabled])',
                );
            }
            // The next battle starts here: warm its picker again.
            warmDeckSelectionWhenIdle();
            // And the Collection's first pages, whose answers are long past their minute.
            inIdleSlot(whileLobbyIdle('Collection', () => Collection?.warmForLobby?.(), inIdleSlot));
        },

        /**
         * Leave the game and land on the sign-in card. Used by the guest tutorial:
         * an anonymous player has no lobby to return to, so Skip / completion / the
         * per-session limit all route here. Tears down in-game UI and the guest
         * socket (resetLobby -> leaveRoom disconnects), then reveals auth-view. The
         * caller (main.js showGuestSignInMoment) layers the guest copy on top.
         */
        switchToAuthView: function () {
            finishGameTransition();
            closeLobbySurfacesForGame({ includeTransientSurfaces: false });
            tearDownInGameUI();

            elements.lobbyView.style.display = 'none';

            document.body.classList.remove('lobby-active');
            elements.gameView.style.display = 'none';
            const authView = document.getElementById('auth-view');
            if (authView) authView.style.display = 'flex';
            if (i18n) i18n.showSelector();

            document.body.classList.remove('game-active');
            const userControls = document.getElementById('user-controls');
            if (userControls) userControls.style.display = 'none';

            resetLobby();
            focusViewDestination(
                authView,
                '#google-signin-btn, button:not([disabled]), [tabindex="0"]',
            );
        },

        openFoundrySurface,
        warmAtIdle,
        closeFoundrySurface,
        updateTutorialButtonMode,
        syncUtilityPlacement
    };
})();

export { Lobby };
